import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate,Link } from 'react-router-dom';
import {
  fetchCoursesRequest,
  fetchModulesRequest,
  fetchChaptersRequest,
  addLessonRequest
} from 'store/actions';
import axiosInstance from 'pages/Utility/axiosInstance';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ClipLoader } from 'react-spinners';
import Breadcrumb from 'components/Common/Breadcrumb2';
import { Button } from 'reactstrap';



const AddLesson = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
const [loading, setLoading] = useState(false);

  const courses = useSelector(state => state.Course.courses);
  const modules = useSelector(state => state.Module.modules);
  const chapters = useSelector(state => state.Chapter.chapters);

  const userId = localStorage.getItem('userId');

  // Fetch Courses
  useEffect(() => {
    dispatch(fetchCoursesRequest());
  }, [dispatch]);

  // Fetch Modules
  const handleCourseChange = (courseId) => {
    dispatch(fetchModulesRequest({ courseId }));
  };

  // Fetch Chapters
  const handleModuleChange = (moduleId) => {
    dispatch(fetchChaptersRequest({ moduleId }));
  };

  // Get Presigned URL
  const getPresignedUrl = async (file) => {
    const response = await axiosInstance.post(
      'V1/common/pre-signed-url',
      { fileName: file.name, fileType: file.type },
      { withCredentials: true }
    );
    return response.data; // { uploadURL, fileKey, publicUrl }
  };

  // Upload File
  const uploadFileToPresignedUrl = async (file, url, onProgress) => {
    await axios.put(url, file, {
      headers: { 'Content-Type': file.type },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        }
      }
    });
  };

  // Validation Schema
  const validationSchema = Yup.object().shape({
    courseId: Yup.string().required('Course is required'),
    moduleId: Yup.string().required('Module is required'),
    chapterId: Yup.string().required('Chapter is required'),
    lessons: Yup.array().of(
      Yup.object().shape({
        title: Yup.string().required('Title is required'),
        orderIndex: Yup.number()
          .typeError('Order index must be a number')
          .min(0, 'Order index cannot be negative')
          .required('Order index is required'),
        contentType: Yup.string().required('Content type is required'),
        duration: Yup.number()
          .when('contentType', {
            is: 'video',
            then: (schema) =>
              schema
                .typeError('Duration must be a number')
                .min(0, 'Duration cannot be negative')
                .required('Duration is required'),
            otherwise: (schema) => schema.notRequired()
          }),
        publicUrl: Yup.string()
          .when('contentType', {
            is: 'file',
            then: (schema) => schema.required('File is required'),
            otherwise: (schema) => schema.required('Video URL is required')
          })
      })
    )
  });

  const initialValues = {
    courseId: '',
    moduleId: '',
    chapterId: '',
    lessons: [
      { title: '', orderIndex: '', contentType: 'video', publicUrl: '', duration: '', file: null, uploading: false, progress: 0 }
    ]
  };

  // Submit Form
  const handleSubmit = (values) => {
    setLoading(true);
    const submissionData = {
      courseId: values.courseId,
      moduleId: values.moduleId,
      chapterId: values.chapterId,
      lessons: values.lessons.map(lesson => {
        const lessonData = {
          title: lesson.title,
          createdBy: userId,
          orderIndex: Number(lesson.orderIndex),
          contentType: lesson.contentType,
          contentURL: lesson.publicUrl
        };
        if (lesson.contentType === 'video') {
          lessonData.duration = Number(lesson.duration);
        }
        return lessonData;
      })
    };

    dispatch(addLessonRequest(submissionData, (err, res) => {
      setLoading(false);
      if (err) {
        // toast.error('Failed to add lessons. Try again.');
      } else {
        // toast.success('Lessons added successfully!');
        setTimeout(() => navigate('/lessons'), 1500);
      }
    }));
  };

  return (
    <div className="page-content container-fluid">

            <div className="d-flex justify-content-between align-items-center mx-3 mb-4">
        <Breadcrumb
          title="Add Lesson"
          breadcrumbItems={[
            { title: 'Dashboard', link: '/dashboard' },
            { title: 'Lessons', link: '/lessons' },
            { title: 'Add Lesson', link: '#' },
          ]}
        />
        <Link to="/lessons">
          <Button color="secondary">Back</Button>
        </Link>
      </div>
      
      <h5>Add New Lesson</h5>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
<Form>
  {/* Grid for the entire form */}
  <div className="d-grid gap-3" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
    
    {/* Course */}
    <div>
      <label>Course <span className="text-danger">*</span></label>
      <Select
        value={values.courseId ? { value: values.courseId, label: courses.data.find(c => c._id === values.courseId)?.title } : null}
        onChange={(selected) => {
          setFieldValue('courseId', selected?.value || '');
          setFieldValue('moduleId', '');
          setFieldValue('chapterId', '');
          handleCourseChange(selected?.value || '');
        }}
        options={courses.data?.map(c => ({ value: c._id, label: c.title })) || []}
        placeholder="Select Course"
        isSearchable
        isClearable
      />
      <ErrorMessage name="courseId" component="div" className="text-danger" />
    </div>

    {/* Module */}
    <div>
      <label>Module <span className="text-danger">*</span></label>
      <Select
        value={values.moduleId ? { value: values.moduleId, label: modules.data.find(m => m._id === values.moduleId)?.title } : null}
        onChange={(selected) => {
          setFieldValue('moduleId', selected?.value || '');
          setFieldValue('chapterId', '');
          handleModuleChange(selected?.value || '');
        }}
        options={modules.data?.map(m => ({ value: m._id, label: m.title })) || []}
        placeholder="Select Module"
        isSearchable
        isClearable
        isDisabled={!values.courseId}
      />
      <ErrorMessage name="moduleId" component="div" className="text-danger" />
    </div>

    {/* Chapter */}
    <div>
      <label>Chapter <span className="text-danger">*</span></label>
      <Select
        value={values.chapterId ? { value: values.chapterId, label: chapters.data.find(ch => ch._id === values.chapterId)?.title } : null}
        onChange={(selected) => setFieldValue('chapterId', selected?.value || '')}
        options={chapters.data?.map(ch => ({ value: ch._id, label: ch.title })) || []}
        placeholder="Select Chapter"
        isSearchable
        isClearable
        isDisabled={!values.moduleId}
      />
      <ErrorMessage name="chapterId" component="div" className="text-danger" />
    </div>
  </div>

  {/* Lessons */}
<FieldArray name="lessons">
  {({ push, remove }) => (
    <div className="mt-4">
      <h5>Lessons</h5>

      {values.lessons.map((lesson, idx) => (
        <div
          key={idx}
          className="border p-3 mb-3 rounded position-relative"
          style={{
            backgroundColor: idx % 2 === 0 ? "#f9fafc" : "#eef3f9",
            transition: "background-color 0.3s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          {/* Lesson header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold mb-0 text-primary">
              Lesson {idx + 1}
            </h6>
            {values.lessons.length > 1 && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => remove(idx)}
              >
                ×
              </button>
            )}
          </div>

          {/* Lesson Fields Grid */}
          <div
            className="d-grid gap-3"
            style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
          >
            {/* Title */}
            <div>
              <label>
                Title <span className="text-danger">*</span>
              </label>
              <Field name={`lessons.${idx}.title`} className="form-control" />
              <ErrorMessage
                name={`lessons.${idx}.title`}
                component="div"
                className="text-danger"
              />
            </div>

            {/* Content Type */}
            <div>
              <label>
                Content Type <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name={`lessons.${idx}.contentType`}
                className="form-control"
                onChange={(e) => {
                  const value = e.target.value;
                  setFieldValue(`lessons.${idx}.contentType`, value);
                  setFieldValue(`lessons.${idx}.publicUrl`, "");
                  setFieldValue(`lessons.${idx}.duration`, "");
                }}
              >
                <option value="video">Video</option>
                <option value="file">File</option>
              </Field>
              <ErrorMessage
                name={`lessons.${idx}.contentType`}
                component="div"
                className="text-danger"
              />
            </div>

            {/* Order Index */}
            <div>
              <label>
                Order Index <span className="text-danger">*</span>
              </label>
              <Field
                name={`lessons.${idx}.orderIndex`}
                type="number"
                min="0"
                className="form-control"
              />
              <ErrorMessage
                name={`lessons.${idx}.orderIndex`}
                component="div"
                className="text-danger"
              />
            </div>
          </div>

          {/* Next row for Video/File Fields */}
          <div
            className="d-grid gap-3 mt-3"
            style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
          >
            {lesson.contentType === "video" ? (
              <>
                <div>
                  <label>
                    Video URL <span className="text-danger">*</span>
                  </label>
                  <Field
                    name={`lessons.${idx}.publicUrl`}
                    className="form-control"
                    placeholder="Enter video URL"
                  />
                  <ErrorMessage
                    name={`lessons.${idx}.publicUrl`}
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div>
                  <label>
                    Duration (minutes) <span className="text-danger">*</span>
                  </label>
                  <Field
                    name={`lessons.${idx}.duration`}
                    type="number"
                    min="0"
                    className="form-control"
                  />
                  <ErrorMessage
                    name={`lessons.${idx}.duration`}
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div></div>
              </>
            ) : (
              <div>
                <label>
                  Upload File <span className="text-danger">*</span>
                </label>
                {!lesson.publicUrl ? (
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      setFieldValue(`lessons.${idx}.uploading`, true);
                      setFieldValue(`lessons.${idx}.progress`, 0);

                      try {
                        const { uploadURL, publicUrl } =
                          await getPresignedUrl(file);

                        await uploadFileToPresignedUrl(
                          file,
                          uploadURL,
                          (progress) => {
                            setFieldValue(`lessons.${idx}.progress`, progress);
                          }
                        );

                        setFieldValue(`lessons.${idx}.publicUrl`, publicUrl);
                        setFieldValue(`lessons.${idx}.uploading`, false);
                      } catch {
                        setFieldValue(`lessons.${idx}.uploading`, false);
                      }
                    }}
                  />
                ) : (
                  <div className="d-flex align-items-center mt-2">
                    <a
                      href={lesson.publicUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View PDF
                    </a>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger ms-2"
                      onClick={() =>
                        setFieldValue(`lessons.${idx}.publicUrl`, "")
                      }
                    >
                      ×
                    </button>
                  </div>
                )}
                {lesson.uploading && (
                  <div className="mt-1">Uploading: {lesson.progress}%</div>
                )}
                <ErrorMessage
                  name={`lessons.${idx}.publicUrl`}
                  component="div"
                  className="text-danger"
                />
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Add Another Lesson button */}
      {(() => {
        const lastLesson = values.lessons[values.lessons.length - 1];
        const isComplete =
          lastLesson.title &&
          lastLesson.orderIndex !== "" &&
          lastLesson.contentType &&
          ((lastLesson.contentType === "video" &&
            lastLesson.publicUrl &&
            lastLesson.duration !== "") ||
            (lastLesson.contentType === "file" && lastLesson.publicUrl));

        if (isComplete) {
          return (
            <button
              type="button"
              className="btn btn-secondary mb-3"
              onClick={() =>
                push({
                  title: "",
                  orderIndex: "",
                  contentType: "video",
                  publicUrl: "",
                  duration: "",
                  file: null,
                })
              }
            >
              + Add Another Lesson
            </button>
          );
        }
        return null;
      })()}
    </div>
  )}
</FieldArray>


  <div>
   <button 
  type="submit" 
  className="btn btn-primary btn-md ms-3" 
  style={{ minWidth: '100px' }} 
  disabled={loading}
>
  {loading ? <ClipLoader size={16} color="white" /> : 'Submit'}
</button>

  </div>
</Form>

        )}
      </Formik>

      {/* <ToastContainer /> */}
    </div>
  );
};

export default AddLesson;
